import { useMemo } from "react";
import useOnboardingSteps from "../hooks/useOnboardingSteps";
import { Link } from "react-router-dom";
import CheckIcon from "../icons/CheckIcon";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const getStatus = step => {
  let result = "upcoming";

  if (!step) return result;
  else if (["Completed", "Skipped"].includes(step?.status)) return "complete";
  else if (step?.current) return "current";

  return result;
};

export default function HomeVerticalProgress() {
  const { steps } = useOnboardingSteps();

  const allSteps = useMemo(() => {
    return [
      {
        slug: "VIDEO",
        name: "Watch onboarding video",
        description: "This will help you become a pro in minutes",
        status: steps.length
          ? getStatus(steps.find(i => i.step === "VIDEO"))
          : "current",
      },
      {
        slug: "KNOWLEDGE_SOURCES",
        name: "Add your knowledge sources",
        description:
          "Upload websites, files, or connect your Shopify to train your AI agent",
        status: getStatus(steps.find(i => i.step === "KNOWLEDGE_SOURCES")),
        href: "/setup/knowledge-sources",
      },
      {
        slug: "BRANDING",
        name: "Setup your messenger branding & AI persona",
        description: "Add your colors, brand tone and name your AI",
        status: getStatus(steps.find(i => i.step === "BRANDING")),
        href: "/setup/branding",
      },
      {
        slug: "INSTRUCT",
        name: "Instruct your new AI agent",
        description: "Just like you would your support team",
        status: getStatus(steps.find(i => i.step === "INSTRUCT")),
        href: "/setup/branding",
      },
      {
        slug: "INTENTS",
        name: "Add customer intents",
        description:
          "These will teach your AI agent on how to handle customers in specific situations",
        status: getStatus(steps.find(i => i.step === "INTENTS")),
        href: "/setup/intents",
      },
      {
        slug: "ACTIONS",
        name: "Add actions (optional)",
        description:
          "Give your agent end to end automation ability by connecting your Custom API, Shopify, Stripe, or 1000s of apps through Zapier",
        status: getStatus(steps.find(i => i.step === "ACTIONS")),
        href: "/dashboard/integrations",
      },
      {
        slug: "INVITE_TEAM",
        name: "Invite your team",
        description: "Start collaborating with your team",
        status: getStatus(steps.find(i => i.step === "INVITE_TEAM")),
        href: "/settings/add_team",
      },
      {
        slug: "DEPLOY",
        name: "Deploy to your website and other channels",
        description:
          "Livechat on your site and connect your AI agent to your Email, SMS, and Socials",
        status: getStatus(steps.find(i => i.step === "DEPLOY")),
        href: "/setup/deploy",
      },
    ];
  }, [steps]);

  return (
    <nav aria-label="Progress">
      <ol className="overflow-hidden">
        {allSteps.map((step, stepIdx) => (
          <li
            key={step.name}
            className={classNames(
              stepIdx !== allSteps.length - 1 ? "pb-10" : "",
              "relative"
            )}
          >
            {step.status === "complete" ? (
              <>
                {stepIdx !== allSteps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full border-l-2 border-dotted border-gray-300"
                  />
                )}
                <div className="group relative flex items-start">
                  <span className="flex h-9 items-center">
                    <span className="relative z-10 flex size-7 items-center justify-center rounded-full bg-customGreen">
                      <CheckIcon className="size-4 text-white" />
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    {step.href ? (
                      <Link
                        to={step.href}
                        className="text-sm font-medium underline"
                      >
                        {step.name}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium">{step.name}</span>
                    )}
                    <span className="text-sm text-gray-500">
                      {step.description}
                    </span>
                  </span>
                </div>
              </>
            ) : step.status === "current" ? (
              <>
                {stepIdx !== allSteps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full border-l-2 border-dotted border-gray-300"
                  />
                )}
                <div
                  aria-current="step"
                  className="group relative flex items-start"
                >
                  <span aria-hidden="true" className="flex h-9 items-center">
                    <span className="relative z-10 flex size-7 items-center justify-center rounded-full border border-gray-300 bg-white">
                      <span className="size-2.5 rounded-full bg-transparent flex items-center justify-center text-gray-700">
                        {stepIdx + 1}
                      </span>
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    {step.href ? (
                      <Link
                        to={step.href}
                        className="text-sm font-medium underline"
                      >
                        {step.name}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium">{step.name}</span>
                    )}

                    <span className="text-sm text-gray-500">
                      {step.description}
                    </span>
                  </span>
                </div>
              </>
            ) : (
              <>
                {stepIdx !== allSteps.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="absolute left-4 top-4 -ml-px mt-0.5 h-full border-l-2 border-dotted border-gray-300"
                  />
                )}
                <a href={step.href} className="group relative flex items-start">
                  <span aria-hidden="true" className="flex h-9 items-center">
                    <span className="relative z-10 flex size-7 items-center justify-center rounded-full border border-gray-300 bg-white">
                      <span className="size-2.5 rounded-full bg-transparent flex items-center justify-center text-gray-400">
                        {stepIdx + 1}
                      </span>
                    </span>
                  </span>
                  <span className="ml-4 flex min-w-0 flex-col">
                    {step.href ? (
                      <Link
                        to={step.href}
                        className="text-sm font-medium underline text-gray-500"
                      >
                        {step.name}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium">{step.name}</span>
                    )}

                    <span className="text-sm text-gray-500">
                      {step.description}
                    </span>
                  </span>
                </a>
              </>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
